import React, { useEffect } from 'react'
import { FaExclamationTriangle, FaCamera, FaTimes } from 'react-icons/fa'
import { useTranslation } from 'react-i18next'
import { cameraLog } from '@renderer/utils/cameras/cameraLog'

interface CameraWarningModalProps {
  isVisible: boolean
  qualityIssue: 'dark' | 'black' | 'noise' | 'none'
  onClose: () => void
  onRetry: () => void
  onIgnore: () => void
}

export const CameraWarningModal: React.FC<CameraWarningModalProps> = ({
  isVisible,
  qualityIssue,
  onClose,
  onRetry,
  onIgnore
}) => {
  const { t } = useTranslation()

  useEffect(() => {
    if (isVisible) cameraLog.uiErrorShown('CameraWarningModal', `image looks ${qualityIssue}`)
  }, [isVisible, qualityIssue])

  if (!isVisible) return null

  const getWarningMessage = (): string => {
    switch (qualityIssue) {
      case 'black':
        return t(
          'settings.camera.warning.blackImage',
          'Camera is showing a black image. Please check if the lens cover is removed or the camera is properly connected.'
        )
      case 'dark':
        return t(
          'settings.camera.warning.darkImage',
          'Camera image is too dark. Please check lighting conditions or camera settings.'
        )
      case 'noise':
        return t(
          'settings.camera.warning.noiseImage',
          'Camera image quality is poor. Please check camera connection and settings.'
        )
      default:
        return t(
          'settings.camera.warning.generic',
          'Camera has quality issues. Please check camera settings and connection.'
        )
    }
  }

  const getWarningTitle = (): string => {
    switch (qualityIssue) {
      case 'black':
        return t('settings.camera.warning.blackTitle', 'Black Image Detected')
      case 'dark':
        return t('settings.camera.warning.darkTitle', 'Image Too Dark')
      case 'noise':
        return t('settings.camera.warning.noiseTitle', 'Poor Image Quality')
      default:
        return t('settings.camera.warning.genericTitle', 'Camera Issue Detected')
    }
  }

  const getWarningIcon = (): React.ReactNode => {
    switch (qualityIssue) {
      case 'black':
        return <FaCamera className="text-red-500" />
      case 'dark':
        return <FaExclamationTriangle className="text-yellow-500" />
      case 'noise':
        return <FaExclamationTriangle className="text-orange-500" />
      default:
        return <FaExclamationTriangle className="text-red-500" />
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 animate-in fade-in duration-200">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 transform transition-all duration-300 animate-in slide-in-from-bottom-2 scale-95">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center gap-3">
            {getWarningIcon()}
            <h3 className="text-lg font-semibold text-gray-900">{getWarningTitle()}</h3>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors p-1 rounded-full hover:bg-gray-100"
            aria-label="Close warning"
          >
            <FaTimes size={18} />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          <p className="text-gray-600 mb-4 leading-relaxed">{getWarningMessage()}</p>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
            <h4 className="font-medium text-blue-900 mb-2">
              {t('settings.camera.warning.troubleshooting', 'Troubleshooting Tips:')}
            </h4>
            <ul className="text-sm text-blue-800 space-y-1">
              <li>• {t('settings.camera.warning.tip1', 'Ensure camera lens cover is removed')}</li>
              <li>
                • {t('settings.camera.warning.tip2', 'Check if camera is properly connected')}
              </li>
              <li>• {t('settings.camera.warning.tip3', 'Verify adequate lighting in the area')}</li>
              <li>• {t('settings.camera.warning.tip4', 'Try reconnecting the camera')}</li>
            </ul>
          </div>
        </div>

        {/* Actions */}
        <div className="flex gap-3 p-4 border-t border-gray-200">
          <button
            onClick={onRetry}
            className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-lg hover:bg-blue-700 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
          >
            {t('settings.camera.warning.retry', 'Retry Camera')}
          </button>
          <button
            onClick={onIgnore}
            className="flex-1 bg-gray-600 text-white py-2 px-4 rounded-lg hover:bg-gray-700 transition-colors font-medium focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2"
          >
            {t('settings.camera.warning.ignore', 'Ignore')}
          </button>
        </div>
      </div>
    </div>
  )
}
